import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';

import Page4Content from '../components/wsr/Page4Content';
import WsrEmptyPage from '../components/wsr/WsrEmptyPage';
import WsrPageHeader from '../components/wsr/WsrPageHeader';
import WsrSideRail from '../components/wsr/WsrSideRail';
import { Page4Data } from '../components/wsr/types';
import { WSR_PAGES, wsrColors } from '../components/wsr/wsrTokens';

/**
 * Default WSR window: the most recent completed Sunday-to-Sunday week.
 *
 * `end` is the latest Sunday on or before today (i.e., today itself if today
 * is Sunday, otherwise the previous Sunday). `start` is `end` minus 7 days.
 * Both are returned as local-time `YYYY-MM-DD` strings to match the date
 * input format used by the page header.
 */
function getDefaultRange(): { start: string; end: string } {
  const today = new Date();
  const daysSinceSunday = today.getDay();
  const end = new Date(today);
  end.setDate(today.getDate() - daysSinceSunday);
  const start = new Date(end);
  start.setDate(end.getDate() - 7);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const DEFAULT_RANGE = getDefaultRange();

interface FetchState {
  status: 'idle' | 'loading' | 'success' | 'error';
  data: Page4Data | null;
  error: string | null;
}

async function fetchPage4(start: string, end: string): Promise<Page4Data> {
  const url = `/api/wsr/page4?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  const response = await fetch(url);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(body.detail || `API error ${response.status}`);
  }
  return response.json();
}

const WsrReportPage: React.FC = () => {
  const [activePage, setActivePage] = useState(4);
  const [start, setStart] = useState(DEFAULT_RANGE.start);
  const [end, setEnd] = useState(DEFAULT_RANGE.end);
  const [page4State, setPage4State] = useState<FetchState>({
    status: 'idle',
    data: null,
    error: null,
  });

  const isInvalidWindow = start >= end;

  useEffect(() => {
    if (activePage !== 4 || isInvalidWindow) {
      return;
    }
    let cancelled = false;
    setPage4State({ status: 'loading', data: null, error: null });
    fetchPage4(start, end)
      .then((data) => {
        if (!cancelled) {
          setPage4State({ status: 'success', data, error: null });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          setPage4State({ status: 'error', data: null, error: message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activePage, start, end, isInvalidWindow]);

  const activePageMeta = useMemo(
    () => WSR_PAGES.find((p) => p.num === activePage),
    [activePage],
  );

  return (
    <Box sx={{ background: wsrColors.slate50, minHeight: '100%', padding: '8px 4px 24px' }}>
      <WsrPageHeader start={start} end={end} onStart={setStart} onEnd={setEnd} />

      <Box
        sx={{
          marginTop: '24px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
          gap: '20px',
        }}
      >
        <WsrSideRail active={activePage} onChange={setActivePage} />
        <Box sx={{ minWidth: 0 }}>
          {isInvalidWindow && (
            <Alert severity="warning" sx={{ marginBottom: '16px' }}>
              Start date must be before end date.
            </Alert>
          )}

          {activePage === 4 ? (
            <ActivePage4
              state={page4State}
              fallbackTitle={activePageMeta?.label ?? 'Ticket Analysis'}
            />
          ) : (
            <WsrEmptyPage
              pageNum={activePage}
              title={activePageMeta?.label ?? `Page ${activePage}`}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const ActivePage4: React.FC<{ state: FetchState; fallbackTitle: string }> = ({
  state,
  fallbackTitle,
}) => {
  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (state.status === 'error' || !state.data) {
    return (
      <Alert severity="error" sx={{ marginTop: '16px' }}>
        Failed to load {fallbackTitle}: {state.error ?? 'Unknown error'}
      </Alert>
    );
  }
  return <Page4Content data={state.data} />;
};

export default WsrReportPage;
